/**
 * Exchange Rates Hook
 * 
 * Uses Bridge API to get real-time exchange rates for withdrawal calculations.
 * Ref: https://apidocs.bridge.xyz/api-reference/exchange-rates
 * 
 * Supported pairs (as of Oct 2025):
 * - USD ↔ EUR, MXN, BRL
 * - BTC, ETH, SOL, USDT → USD
 */

import { useQuery } from '@tanstack/react-query';
import { getExchangeRate } from '@/lib/exchangeRates';

// Bridge API supported fiat currencies
export type FiatCurrency = 'USD' | 'EUR' | 'MXN' | 'BRL';

// Stablecoins that are pegged 1:1 to USD
export const USD_PEGGED_STABLECOINS = ['USDC', 'USDT', 'USDB', 'DAI', 'PYUSD', 'USDP'] as const;

// Stablecoins that are pegged 1:1 to EUR
export const EUR_PEGGED_STABLECOINS = ['EURC'] as const;

// All supported stablecoins
export const ALL_STABLECOINS = [...USD_PEGGED_STABLECOINS, ...EUR_PEGGED_STABLECOINS] as const;

export type Stablecoin = typeof ALL_STABLECOINS[number];

/**
 * Hook to get USD → EUR exchange rate from Bridge API
 * 
 * Returns:
 * - rate: current USD to EUR rate (e.g., 0.93)
 * - isLoading: loading state
 * - error: error if API call failed
 * - refetch: function to refresh rate
 * - dataUpdatedAt: timestamp when rate was last fetched
 */
export function useUsdToEurRate() {
  return useQuery({
    queryKey: ['exchangeRate', 'USD', 'EUR'],
    queryFn: async () => {
      const rate = await getExchangeRate('USD', 'EUR');
      return rate;
    },
    staleTime: 30 * 1000, // 30 seconds (Bridge updates every ~30s)
    gcTime: 5 * 60 * 1000, // 5 minutes cache
    refetchInterval: 30 * 1000, // Refetch every 30 seconds to match Bridge API update frequency
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    refetchIntervalInBackground: false, // Don't refetch in background
    retry: 2,
    // Fallback to hardcoded rate if API fails
    placeholderData: 0.93,
  });
}

/**
 * Hook to get EUR → USD exchange rate from Bridge API
 */
export function useEurToUsdRate() {
  return useQuery({
    queryKey: ['exchangeRate', 'EUR', 'USD'],
    queryFn: async () => {
      const rate = await getExchangeRate('EUR', 'USD');
      return rate;
    },
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: 30 * 1000, // Refetch every 30 seconds
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
    retry: 2,
    placeholderData: 1.08,
  });
}

/**
 * Calculate total withdrawable balance in target fiat currency
 * 
 * For USD withdrawal:
 * - USD stablecoins: 1:1
 * - EUR stablecoins: EURC × EUR→USD rate
 * 
 * For EUR withdrawal:
 * - EUR stablecoins: 1:1
 * - USD stablecoins: sum × USD→EUR rate
 */
export interface StablecoinBalance {
  currency: string;
  amount: number;
  chain?: string;
}

export function calculateWithdrawableBalance(
  balances: StablecoinBalance[],
  targetFiat: FiatCurrency,
  usdToEurRate: number = 0.93,
  eurToUsdRate: number = 1.08
): {
  total: number;
  breakdown: Array<{
    currency: string;
    amount: number;
    convertedAmount: number;
    rate: number;
  }>;
} {
  const breakdown: Array<{
    currency: string;
    amount: number;
    convertedAmount: number;
    rate: number;
  }> = [];

  let total = 0;

  for (const balance of balances) {
    const currency = balance.currency.toUpperCase();
    const amount = balance.amount;

    if (amount <= 0) continue;

    let convertedAmount: number;
    let rate: number;

    if (targetFiat === 'USD') {
      // Target: USD
      if (USD_PEGGED_STABLECOINS.includes(currency as typeof USD_PEGGED_STABLECOINS[number])) {
        // USD stablecoin → USD: 1:1
        rate = 1;
        convertedAmount = amount;
      } else if (EUR_PEGGED_STABLECOINS.includes(currency as typeof EUR_PEGGED_STABLECOINS[number])) {
        // EURC → USD: use EUR→USD rate
        rate = eurToUsdRate;
        convertedAmount = amount * rate;
      } else {
        // Unknown currency, skip
        continue;
      }
    } else if (targetFiat === 'EUR') {
      // Target: EUR
      if (EUR_PEGGED_STABLECOINS.includes(currency as typeof EUR_PEGGED_STABLECOINS[number])) {
        // EUR stablecoin → EUR: 1:1
        rate = 1;
        convertedAmount = amount;
      } else if (USD_PEGGED_STABLECOINS.includes(currency as typeof USD_PEGGED_STABLECOINS[number])) {
        // USD stablecoin → EUR: use USD→EUR rate
        rate = usdToEurRate;
        convertedAmount = amount * rate;
      } else {
        // Unknown currency, skip
        continue;
      }
    } else {
      // Unsupported target fiat
      continue;
    }

    breakdown.push({
      currency,
      amount,
      convertedAmount,
      rate,
    });

    total += convertedAmount;
  }

  // Truncate to 2 decimal places (Bridge API precision)
  total = Math.floor(total * 100) / 100;

  return { total, breakdown };
}

/**
 * Combined hook for withdrawal balance calculation
 */
export function useWithdrawableBalance(
  balances: StablecoinBalance[],
  targetFiat: FiatCurrency
) {
  const { 
    data: usdToEurRate = 0.93, 
    isLoading: isLoadingUsdEur,
    dataUpdatedAt: usdEurUpdatedAt,
    refetch: refetchUsdEur 
  } = useUsdToEurRate();
  
  const { 
    data: eurToUsdRate = 1.08, 
    isLoading: isLoadingEurUsd,
    dataUpdatedAt: eurUsdUpdatedAt,
    refetch: refetchEurUsd 
  } = useEurToUsdRate();

  const isLoading = isLoadingUsdEur || isLoadingEurUsd;
  
  // Most recent update timestamp
  const lastUpdated = Math.max(usdEurUpdatedAt || 0, eurUsdUpdatedAt || 0);

  const result = calculateWithdrawableBalance(
    balances,
    targetFiat,
    usdToEurRate,
    eurToUsdRate
  );

  // Combined refetch function
  const refetchRates = async () => {
    await Promise.all([refetchUsdEur(), refetchEurUsd()]);
  };

  return {
    ...result,
    isLoading,
    rates: {
      usdToEur: usdToEurRate,
      eurToUsd: eurToUsdRate,
    },
    lastUpdated: lastUpdated > 0 ? new Date(lastUpdated) : null,
    refetchRates,
  };
}

